import { useInfiniteQuery } from '@tanstack/react-query'

import { NO_MORE_DATA, TEMP_USER_ID } from 'constant'
import { useLocale } from 'next-intl'
import { fetchCommunitiesInStream } from 'server-actions/fetch-community'
import { useCommunityLocationPickerStore } from 'store/global/location-picker'
import { useIpLocationStore } from 'store/server-context/ip-location'
import { useSessionStore } from 'store/server-context/session'
import type { ICommunityLocationPickerV2 } from 'type/feed'
import convertLevelType from 'util/convertLevelType'

const SIZE = 6

const useCommunityFeed = (
  pickedLocation: ICommunityLocationPickerV2 | null
) => {
  const locale = useLocale()
  const userSession = useSessionStore(state => state.session)
  const isRefresh = useCommunityLocationPickerStore(state => state.isRefresh)
  const refreshingId = useCommunityLocationPickerStore(
    state => state.refreshingId
  )
  const ipLocation = useIpLocationStore(state => state.ipLocation)

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } =
    useInfiniteQuery({
      queryKey: ['community', refreshingId],
      initialPageParam: '',
      queryFn: async ({ pageParam }) => {
        try {
          const { lat, lng } = ipLocation?.ip_geo_point || { lat: 0, lng: 0 }
          const userId = userSession.guest ? TEMP_USER_ID : userSession.user_id

          const data = await fetchCommunitiesInStream(
            {
              session_id: pageParam,
              is_change_location: !!pickedLocation,
              is_refresh: isRefresh,
              location_picker: pickedLocation
                ? {
                    pick_content_ids: pickedLocation.content_ids || [],
                    level_type: convertLevelType(pickedLocation.level_type),
                    location_id: pickedLocation?.location?.id || ''
                  }
                : null,
              location_id: pickedLocation?.location?.id || '',
              user_id: userId,
              user_coordinate: {
                lat: `${lat}`,
                lng: `${lng}`
              },
              size: SIZE
            },
            locale
          )

          useCommunityLocationPickerStore.setState({
            isRefresh: false,
            pickedLocation: null
          })

          return {
            community_list: data.communities || [],
            session_id: data.session.id || ''
          }
        } catch (error) {
          console.error(error)
          return {
            community_list: [],
            session_id: NO_MORE_DATA
          }
        }
      },
      getNextPageParam: lastPage => {
        if (
          lastPage.session_id === NO_MORE_DATA ||
          !lastPage.session_id ||
          lastPage.community_list.length === 0
        ) {
          return undefined
        }
        return lastPage.session_id
      },
      select(data) {
        return data.pages.flatMap(page => page.community_list || [])
      }
    })

  return {
    data: data || [],
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading
  }
}

export default useCommunityFeed
